import {
  type BaseSyntheticEvent,
  type FocusEventHandler,
  useEffect,
  useMemo,
  useState,
} from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import {
  type Control,
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";

import { toastError, toastSuccess } from "@alpacahq/ui/old";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import { invalidateOrderRelatedQueries } from "~/v2/api/invalidateOrderRelatedQueries";
import type { Asset } from "~/v2/api/rest";
import type { Account, Margin, Position, Product } from "~/v2/api/rest/account";
import type { Quote } from "~/v2/api/rest/data";
import { createOrder } from "~/v2/api/rest/order";
import type {
  CryptoOrderType,
  CryptoQuantityType,
  CryptoTimeInForce,
  Side,
} from "~/v2/constants/cryptoOrder";
import {
  amplitudeTradeTracking,
  type OrderEvent,
} from "~/v2/pages/dashboard/tradeV2/amplitudeTradeTracking";
import { concatFormErrorMessages } from "../shared/utils";
import { useCryptoAvailableQuantity } from "./hooks/useCryptoAvailableQuantity";
import { useCryptoBuyingPower } from "./hooks/useCryptoBuyingPower";
import { useCryptoEstimatedCost } from "./hooks/useCryptoEstimatedCost";
import { useCryptoEstimatedQuantity } from "./hooks/useCryptoEstimatedQuantity";
import { type QuoteDisplay, useCryptoPrice } from "./hooks/useCryptoPrice";
import {
  getCryptoTimeInForceOptions,
  useCryptoTimeInForceOptions,
} from "./hooks/useCryptoTimeInForceOptions";
import {
  type CryptoOrderTicketSchema,
  cryptoOrderTicketSchemaSuperRefinement,
  isTimeInForceValid,
  zCryptoOrderTicketSchema,
} from "./schema";
import {
  createCryptoOrderObject,
  findPosition,
  getDecimalPlaces,
  getIsSubmitDisabled,
  parseCryptoPair,
  toPositionSymbol,
} from "./utils";

const SUBMIT_COMPLETED_TIMEOUT = 500;

const DEFAULT_FORM_VALUES: Partial<CryptoOrderTicketSchema> = {
  orderType: "market",
  timeInForce: "gtc",
  quantityType: "base",
  quantity: 1,
};

interface UseCryptoOrderTicketControllerProps {
  account: Account | undefined;
  product: Product | undefined;
  asset: Asset | undefined;
  margin: Margin | undefined;
  quote: Quote | undefined;
  positions: Position[] | undefined;
  symbol: string;
  initialQuantity?: number;
}

export const useCryptoOrderTicketController = ({
  account,
  product,
  asset,
  quote,
  margin,
  positions,
  symbol,
  initialQuantity,
}: UseCryptoOrderTicketControllerProps): UseCryptoOrderTicketControllerReturn => {
  const [isSubmitCompleted, setIsSubmitCompleted] = useState(false);

  const {
    setValue,
    watch,
    control,
    handleSubmit,
    reset: resetForm,
  } = useForm<CryptoOrderTicketSchema>({
    defaultValues: {
      ...DEFAULT_FORM_VALUES,
      quantity: initialQuantity && initialQuantity > 0 ? initialQuantity : 1,
    },
    mode: "onChange",
    resolver: zodResolver(
      zCryptoOrderTicketSchema.superRefine(
        cryptoOrderTicketSchemaSuperRefinement,
      ),
    ),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: symbol is the trigger: the form is reset to defaults when the user switches asset. The body writes rather than reads it, which is why the rule cannot see the intent.
  useEffect(() => {
    resetForm({
      ...DEFAULT_FORM_VALUES,
      quantity: initialQuantity && initialQuantity > 0 ? initialQuantity : 1,
    });
  }, [symbol, initialQuantity, resetForm]);

  const orderType = watch("orderType");
  const quantityType = watch("quantityType");
  const quantity = watch("quantity");
  const limitPrice = watch("limitPrice");
  const stopPrice = watch("stopPrice");
  const timeInForce = watch("timeInForce");

  const isNotional = quantityType === "quote" && orderType === "market";
  const isQuantityTypeDisabled = orderType !== "market";

  const orderEventData: OrderEvent = {
    order_type: orderType,
    asset_symbol: symbol,
    asset_class: "crypto",
    is_notional: isNotional,
    qty: isNotional ? undefined : quantity,
    amount: isNotional ? quantity : undefined,
    time_in_force: timeInForce,
  };

  // Currency
  const { baseCurrency, quoteCurrency } = useMemo(
    () => parseCryptoPair(symbol),
    [symbol],
  );
  const isUsdPair = quoteCurrency === "USD";

  // Quantity input
  const isNotionalUsd = quantityType === "quote" && isUsdPair;
  const quantityLabel = quantityType === "base" ? "Quantity" : "Amount";
  const quantityTypeOptions: { label: string; value: CryptoQuantityType }[] = [
    { label: baseCurrency, value: "base" },
    { label: quoteCurrency, value: "quote" },
  ];
  const quantityIncrement =
    quantityType === "base"
      ? asset?.min_trade_increment
      : asset?.price_increment;
  const quantityStep = quantityIncrement ?? "any";
  const quantityMaxDecimals = getDecimalPlaces(quantityIncrement);

  // Price input
  const priceMaxDecimals = getDecimalPlaces(asset?.price_increment);

  const basePosition = useMemo(
    () =>
      positions
        ? findPosition(positions, toPositionSymbol(baseCurrency))
        : undefined,
    [positions, baseCurrency],
  );

  const quotePosition = useMemo(
    () =>
      positions && quoteCurrency && !isUsdPair
        ? findPosition(positions, toPositionSymbol(quoteCurrency))
        : undefined,
    [positions, quoteCurrency, isUsdPair],
  );

  const { midPrice, priceChange, quoteDisplay } = useCryptoPrice({ quote });

  const quoteLatestUpdated = useMemo(() => {
    if (!quote?.bid_timestamp) {
      return undefined;
    }
    return moment(quote.bid_timestamp)
      .tz("America/New_York")
      .format("MM/DD HH:mm:ss");
  }, [quote?.bid_timestamp]);

  const { availableBase, availableQuote } = useCryptoAvailableQuantity({
    basePosition,
    quotePosition,
  });

  const { nonMarginBuyingPower: rawNonMarginBuyingPower } =
    useCryptoBuyingPower({ margin });
  const nonMarginBuyingPower = isUsdPair ? rawNonMarginBuyingPower : undefined;

  const { estimatedCost } = useCryptoEstimatedCost({
    isNotional,
    quantity,
    orderType,
    limitPrice,
    midPriceValue: midPrice.value,
  });

  const { estimatedQuantity } = useCryptoEstimatedQuantity({
    isNotional,
    quantity,
    midPriceValue: midPrice.value,
  });

  const { timeInForceOptions } = useCryptoTimeInForceOptions({ orderType });

  const onOrderTypeChange = (newOrderType: CryptoOrderType) => {
    setValue("orderType", newOrderType);
    amplitudeTradeTracking.orderTypeChanged(newOrderType);
    setValue("limitPrice", undefined);
    setValue("stopPrice", undefined);

    if (newOrderType !== "market") {
      setValue("quantityType", "base");
    }

    if (!isTimeInForceValid({ timeInForce, orderType: newOrderType })) {
      const validOptions = getCryptoTimeInForceOptions({
        orderType: newOrderType,
      });
      if (validOptions.length > 0) {
        setValue("timeInForce", validOptions[0].value);
      }
    }
  };

  const onQuantityTypeChange = (newQuantityType: CryptoQuantityType) => {
    setValue("quantityType", newQuantityType);
  };

  const onTimeInForceChange = (newTimeInForce: CryptoTimeInForce) => {
    setValue("timeInForce", newTimeInForce);
  };

  const queryClient = useQueryClient();
  const invalidateOrderAndPositionQueries = () => {
    invalidateOrderRelatedQueries(queryClient);
  };

  const mutation = useMutationRequest(
    ({
      product,
      ...data
    }: CryptoOrderTicketSchema & {
      product: Product;
      side: Side;
    }) =>
      createOrder(
        account?.id ?? "",
        createCryptoOrderObject({ ...data, symbol }),
        product,
      ),
    {
      onSuccess: () => {
        toastSuccess("Your order has been placed successfully");
        invalidateOrderAndPositionQueries();
        setIsSubmitCompleted(true);
        setTimeout(() => {
          setIsSubmitCompleted(false);
          resetForm(DEFAULT_FORM_VALUES);
        }, SUBMIT_COMPLETED_TIMEOUT);
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Order failed: ${errorMessage}`);
      },
    },
  );

  const isSubmitLoading = mutation.isPending;

  const isSubmitDisabledValue = useMemo(
    () =>
      isSubmitCompleted ||
      isSubmitLoading ||
      getIsSubmitDisabled({
        account,
        asset,
        quote,
        product,
        quantity,
        orderType,
        limitPrice,
        stopPrice,
      }),
    [
      account,
      asset,
      quote,
      product,
      quantity,
      orderType,
      limitPrice,
      stopPrice,
      isSubmitCompleted,
      isSubmitLoading,
    ],
  );

  const onValidSubmit =
    (submitSide: Side): SubmitHandler<CryptoOrderTicketSchema> =>
    (data) => {
      if (!mutation.isPending && product) {
        mutation.mutate({ ...data, side: submitSide, product });
        amplitudeTradeTracking.confirmOrderClicked(orderEventData);
      }
    };

  const onInvalidSubmit =
    (_side: Side): SubmitErrorHandler<CryptoOrderTicketSchema> =>
    (errors) =>
      toastError(`Invalid Order: ${concatFormErrorMessages(errors)}`);

  const onSubmit = (submitSide: Side) =>
    handleSubmit(onValidSubmit(submitSide), onInvalidSubmit(submitSide));

  const onQuantityBlur: FocusEventHandler<HTMLInputElement> = (event) =>
    amplitudeTradeTracking.quantityChanged(event.target.value);

  return {
    // Form control & submission
    control,
    onSubmit,
    onQuantityBlur,
    isSubmitDisabled: isSubmitDisabledValue,
    isSubmitLoading,
    isSubmitCompleted,

    // Price & quote
    midPrice,
    priceChange,
    quoteDisplay,
    quoteLatestUpdated,

    // Estimated values
    estimatedCost,
    estimatedQuantity,

    // Buying power
    nonMarginBuyingPower,

    // USD pair flag
    isUsdPair,

    // Order type
    orderType,
    onOrderTypeChange,

    // Quantity
    quantityType,
    onQuantityTypeChange,
    isQuantityTypeDisabled,
    isNotionalUsd,
    quantityLabel,
    quantityTypeOptions,
    quantityStep,
    quantityMaxDecimals,

    // Time in force
    timeInForce,
    onTimeInForceChange,
    timeInForceOptions,

    // Available quantity
    availableBase,
    availableQuote,

    // Currency info
    baseCurrency,
    quoteCurrency,

    // Decimal precision
    priceMaxDecimals,
  };
};

export type UseCryptoOrderTicketControllerReturn = {
  // Form control & submission
  control: Control<CryptoOrderTicketSchema>;
  onSubmit: (side: Side) => (event?: BaseSyntheticEvent) => Promise<void>;
  onQuantityBlur: FocusEventHandler<HTMLInputElement>;
  isSubmitDisabled: boolean;
  isSubmitLoading: boolean;
  isSubmitCompleted: boolean;

  // Price & quote
  midPrice: { value: number; display: string };
  priceChange: { value: number; percentage: string };
  quoteDisplay: QuoteDisplay;
  quoteLatestUpdated: string | undefined;

  // Estimated values
  estimatedCost: string;
  estimatedQuantity: string;

  // Buying power
  nonMarginBuyingPower: string | undefined;

  // USD pair flag
  isUsdPair: boolean;

  // Order type
  orderType: CryptoOrderType;
  onOrderTypeChange: (orderType: CryptoOrderType) => void;

  // Quantity
  quantityType: CryptoQuantityType;
  onQuantityTypeChange: (quantityType: CryptoQuantityType) => void;
  isQuantityTypeDisabled: boolean;
  isNotionalUsd: boolean;
  quantityLabel: string;
  quantityTypeOptions: { label: string; value: CryptoQuantityType }[];
  quantityStep: string;
  quantityMaxDecimals: number | undefined;

  // Time in force
  timeInForce: CryptoTimeInForce;
  onTimeInForceChange: (timeInForce: CryptoTimeInForce) => void;
  timeInForceOptions: {
    label: string;
    subLabel?: string;
    value: CryptoTimeInForce;
  }[];

  // Available quantity
  availableBase: string | undefined;
  availableQuote: string | undefined;

  // Currency info
  baseCurrency: string;
  quoteCurrency: string;

  // Decimal precision
  priceMaxDecimals: number | undefined;
};
